import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import Segmented from "@/components/enhance/Segmented";
import { CHOIR_PHASES, makeBgvChoir, usesChoir } from "@/lib/bgvApi";
import { safeApiError } from "@/lib/safeErrorText";

const GENDERS = [["mixed", "Mixed"], ["male", "Male"], ["female", "Female"]];

function Status({ choir, dirty }) {
  if (choir.status === "running" || choir.status === "queued") {
    return <p data-testid="bgv-choir-progress" className="flex items-center gap-2 text-[13px] text-amber-100"><Loader2 className="h-4 w-4 animate-spin" /> {CHOIR_PHASES[choir.phase] || "Getting the choir ready"}…</p>;
  }
  if (choir.status === "error") return <p data-testid="bgv-choir-error" className="flex items-center gap-2 text-[13px] text-rose-200"><AlertTriangle className="h-4 w-4" /> {choir.error}</p>;
  if (choir.status === "done") {
    return (
      <p data-testid="bgv-choir-done" className="flex items-center gap-2 text-[13px] text-emerald-200">
        <CheckCircle2 className="h-4 w-4" /> The AI choir is on its own track{dirty ? " — you changed its settings, make it again to hear them" : ""}.
      </p>
    );
  }
  return null;
}

/** A choir sung over your music by Suno, split out and lined up with your song (uses Suno credits each time). */
export default function AiChoirPanel({ doc, settings, change, flush, onStarted }) {
  const [busy, setBusy] = useState(false);
  const choir = doc.choir || {};
  const running = choir.status === "running" || choir.status === "queued";
  const setAi = (patch) => change((s) => ({ ...s, ai: { ...s.ai, ...patch } }));
  const make = async () => {
    setBusy(true);
    try { await flush(); onStarted(await makeBgvChoir(doc.id)); } catch (e) { toast.error("Couldn't start the choir", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  if (!doc.can_choir) {
    return (
      <section data-testid="bgv-choir" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/60 p-5 sm:p-6">
        <h2 className="font-display text-base font-semibold md:text-lg">AI choir</h2>
        <p data-testid="bgv-choir-unavailable" className="mt-2 text-[13px] text-white/50">{doc.info?.has_music ? "Needs your ELPM music engine connection." : "Needs music to sing over — start from a library song or a take recorded over music."}</p>
      </section>
    );
  }
  return (
    <section data-testid="bgv-choir" className={`space-y-4 rounded-3xl border p-5 sm:p-6 ${usesChoir(settings) ? "border-amber-300/25 bg-amber-300/[0.04]" : "border-white/[0.08] bg-[#0a0d1c]/80"}`}>
      <div>
        <h2 className="font-display text-base font-semibold md:text-lg">AI choir</h2>
        <p className="mt-1 text-[12px] text-white/50">Sung over your music by AI, then cut to the sections you chose. Uses ELPM credits each time you make it.</p>
      </div>
      <Segmented label="Choir" value={settings.ai.gender} options={GENDERS} onChange={(gender) => setAi({ gender })} testId="bgv-choir-gender" disabled={running} />
      <label className="block">
        <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">Extra style words (optional)</span>
        <input data-testid="bgv-choir-style" value={settings.ai.style} maxLength={200} onChange={(e) => setAi({ style: e.target.value })} disabled={running}
          placeholder="e.g. Jamaican church choir, big hallelujahs" className="w-full rounded-full border border-white/10 bg-black/40 px-4 py-2 text-[13px] text-white placeholder:text-white/30 focus:outline-none" />
      </label>
      <Status choir={choir} dirty={doc.choir_dirty} />
      <button type="button" data-testid="bgv-choir-make" onClick={make} disabled={busy || running}
        className="inline-flex items-center gap-2 rounded-full bg-amber-300 px-5 py-2.5 text-[13px] font-semibold text-black transition-transform duration-200 hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {choir.status === "done" || choir.status === "error" ? "Make the choir again" : "Make the AI choir"}
      </button>
    </section>
  );
}
